import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// The Qmeter tokens and typeface stay global: the layout renderer draws a hall
// board or a kiosk design with them, in the Designs editor as on the machine.
// The staff screens' own design is ui-tokens.css + ui.css. The rest of the Qmeter CSS
// (legacy.css) is loaded only by the customer-facing pages.
import './styles/fonts.css'
// The Designs fonts: declared here, fetched by a browser only when a design uses one.
import './fonts/fonts.css'
import './styles/tokens.css'
// HeroUI (the staff design system since Nov 2026): Tailwind's reset, HeroUI's components and
// theme variables, then our theme override. ui-tokens.css maps the old names onto them.
import './styles/heroui.css'
import './styles/ui-tokens.css'
import './styles/ui.css'
import './styles/surfaces.css'
import { ADDRESSED, App } from './App'
import { api } from './lib/api'
import { reportClientErrors } from './lib/clientErrors'
import { rememberArrival, startFailover, startStaffFailover } from './lib/failover'
import { applyTheme, readTheme } from './ui/theme'

// The staff screens' theme, and their uncaught errors sent to the server's log; a
// machine's page keeps its own look and logs through its host.
if (!ADDRESSED.test(window.location.pathname) && window.location.pathname !== '/t') {
  applyTheme(readTheme())
  reportClientErrors(window, api.clientError, () => window.location.pathname)
}

// A machine opened at the address an administrator copied to it carries its key
// once, as ?k=: trade it for the cookie every later request brings (the live
// channel and pictures included), and take it out of the address bar before
// anything is drawn. See internal/api/screenkey.go.
async function enrolMachine(): Promise<void> {
  const address = ADDRESSED.exec(window.location.pathname)
  const params = new URLSearchParams(window.location.search)
  const key = params.get('k')
  const branchCode = address?.[2]
  const unitCode = address?.[3]
  if (!branchCode || !unitCode || !key) return
  try {
    await fetch(
      `/api/v1/screen/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}/enrol`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
        credentials: 'same-origin',
      },
    )
  } catch {
    // The screen still starts; its requests then say the key is missing.
  }
  params.delete('k')
  const query = params.toString()
  window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash)
}

const root = document.getElementById('root')
if (!root) throw new Error('the page has no root element to mount into')
const mount = root

// Kept before enrolMachine drops ?k=: the parent needs the key if the branch server fails.
const machine = ADDRESSED.exec(window.location.pathname)
if (machine?.[2] && machine[3]) rememberArrival(machine[2], machine[3])

void enrolMachine().finally(() => {
  createRoot(mount).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
  if (machine?.[2] && machine[3]) startFailover(machine[2], machine[3])
  else if (!machine && window.location.pathname !== '/t') startStaffFailover()
})
